import qs from 'qs'
import {
  checkHttpStatus,
  getApiBaseUrl,
  getApiBaseUrlWithOrg,
  getApiHeadersFromToken
} from 'utils/api'

export const beginQuest = async (
  apiData,
  courseEnrollmentId,
  levelId,
  compiledQuestId
) => {
  try {
    const response = checkHttpStatus(
      await fetch(
        `${getApiBaseUrlWithOrg(apiData.orgId)}/user/compiled_quests`,
        {
          method: 'POST',
          body: JSON.stringify({
            user_compiled_quest: {
              course_enrollment_id: courseEnrollmentId,
              level_id: levelId,
              compiled_quest_id: compiledQuestId
            }
          }),
          headers: getApiHeadersFromToken(apiData.token)
        }
      )
    )

    return await response.json()
  } catch (err) {
    return false
  }
}

export const dropQuest = async (apiData, userCompiledQuestId) => {
  try {
    checkHttpStatus(
      await fetch(
        `${getApiBaseUrlWithOrg(apiData.orgId)}/user/compiled_quests/` +
          userCompiledQuestId,
        {
          method: 'DELETE',
          headers: getApiHeadersFromToken(apiData.token)
        }
      )
    )
    return true
  } catch (err) {
    return false
  }
}

export const fetchUserQuestActivity = async (
  apiData,
  userQuestActivityId,
  studentId
) => {
  const url = `${getApiBaseUrlWithOrg(apiData.orgId)}/${
    !apiData.isMentor ? 'user' : `users/${studentId}`
  }/quest_activities/${userQuestActivityId}`

  try {
    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    return await response.json()
  } catch (err) {
    throw err
  }
}

export const createUserQuestActivity = async (
  apiData,
  userCompiledQuestId,
  activityId
) => {
  try {
    const response = checkHttpStatus(
      await fetch(
        `${getApiBaseUrl()}/api/organizations/${
          apiData.orgId
        }/user/compiled_quests/${userCompiledQuestId}/activities`,
        {
          method: 'POST',
          body: JSON.stringify({
            user_quest_activity: {
              activity_id: activityId
            }
          }),
          headers: getApiHeadersFromToken(apiData.token)
        }
      )
    )

    return await response.json()
  } catch (error) {
    return null
  }
}

export const completeUserQuestActivity = async (
  apiData,
  userCompiledQuestId,
  userQuestActivityId
) => {
  const url =
    `${getApiBaseUrl()}/api/organizations/${apiData.orgId}` +
    `/user/compiled_quests/${userCompiledQuestId}/activities/${userQuestActivityId}/complete`

  try {
    const response = checkHttpStatus(
      await fetch(url, {
        method: 'PUT',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    return await response.json()
  } catch (error) {
    return null
  }
}

export const submitUserQuestActivityForReview = async (
  apiData,
  compiledQuestId,
  userQuestActivityId
) => {
  try {
    const response = checkHttpStatus(
      await fetch(
        `${getApiBaseUrl()}/api/organizations/${
          apiData.orgId
        }/user/compiled_quests/${compiledQuestId}/activities/${userQuestActivityId}/await_approval`,
        {
          method: 'PUT',
          headers: getApiHeadersFromToken(apiData.token)
        }
      )
    )

    return await response.json()
  } catch (error) {
    return null
  }
}

export const mutateUserQuestActivityNote = async (
  apiData,
  userCompiledQuestId,
  userQuestActivityId,
  noteId,
  visibility,
  description
) => {
  let url = `${getApiBaseUrlWithOrg(
    apiData.orgId
  )}/user/compiled_quests/${userCompiledQuestId}/activities/${userQuestActivityId}/notes`

  if (noteId) {
    url += `/${noteId}`
  }

  try {
    const response = checkHttpStatus(
      await fetch(url, {
        method: noteId ? 'PATCH' : 'POST',
        headers: getApiHeadersFromToken(apiData.token),
        body: JSON.stringify({
          note: {
            visibility,
            description
          }
        })
      })
    )

    return await response.json()
  } catch (err) {
    throw err
  }
}

export const deleteUserQuestActivityNote = async (
  apiData,
  userCompiledQuestId,
  userQuestActivityId,
  noteId
) => {
  let url = `${getApiBaseUrlWithOrg(
    apiData.orgId
  )}/user/compiled_quests/${userCompiledQuestId}/activities/${userQuestActivityId}/notes/${noteId}`

  try {
    checkHttpStatus(
      await fetch(url, {
        method: 'DELETE',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    return true
  } catch (err) {
    throw err
  }
}

export const addQuestLogEventMessage = async (
  apiData,
  studentId,
  courseSectionId,
  levelId,
  questId,
  text
) => {
  try {
    const url = `${getApiBaseUrlWithOrg(apiData.orgId)}/${
      !apiData.isMentor ? 'user' : `users/${studentId}`
    }/course_sections/${courseSectionId}/quest_log_events`

    const response = checkHttpStatus(
      await fetch(url, {
        method: 'POST',
        body: JSON.stringify({
          quest_log_event: {
            level_id: levelId,
            quest_id: questId,
            text
          }
        }),
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    return await response.json()
  } catch (error) {
    return null
  }
}

export const addUserWorkItem = async (
  apiData,
  userCompiledQuestId,
  activityId,
  work_item,
  itemType
) => {
  try {
    const response = checkHttpStatus(
      await fetch(
        `${getApiBaseUrl()}/api/organizations/${
          apiData.orgId
        }/user/compiled_quests/${userCompiledQuestId}/activities/${activityId}/work_items`,
        {
          method: 'POST',
          body: JSON.stringify({
            work_item: {
              item_type: itemType,
              ...work_item
            }
          }),
          headers: getApiHeadersFromToken(apiData.token)
        }
      )
    )

    const uwi = await response.json()
    return uwi.user_work_item
  } catch (error) {
    return null
  }
}

export const deleteUserWorkItem = async (
  apiData,
  userCompiledQuestId,
  activityId,
  userWorkItemId,
  newFileArtifact
) => {
  try {
    const params = qs.stringify({
      q: {
        new_artifact_file: newFileArtifact || undefined
      }
    })
    const url = `${getApiBaseUrlWithOrg(
      apiData.orgId
    )}/user/compiled_quests/${userCompiledQuestId}/activities/${activityId}/work_items/${userWorkItemId}/?${params}`

    checkHttpStatus(
      await fetch(url, {
        method: 'DELETE',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )

    return true
  } catch (error) {
    return false
  }
}

export const patchUserWorkItem = async (
  apiData,
  userCompiledQuestId,
  activityId,
  work_item
) => {
  try {
    const response = checkHttpStatus(
      await fetch(
        `${getApiBaseUrl()}/api/organizations/${
          apiData.orgId
        }/user/compiled_quests/${userCompiledQuestId}/activities/${activityId}/work_items/${
          work_item.id
        }${!!work_item.attachment ? '/file' : ''}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            work_item
          }),
          headers: getApiHeadersFromToken(apiData.token)
        }
      )
    )

    const uwi = await response.json()
    return uwi.user_work_item
  } catch (error) {
    return null
  }
}

export const fetchMentorResources = async (
  apiData,
  courseSectionId,
  levelId,
  activityId
) => {
  try {
    const filterParams = qs.stringify({
      q: {
        level_id_eq: levelId,
        activity_id_eq: activityId
      }
    })
    const response = checkHttpStatus(
      await fetch(
        `${getApiBaseUrl()}/api/organizations/${
          apiData.orgId
        }/course_sections/${courseSectionId}/mentor_activity_resources?${filterParams}`,
        {
          method: 'GET',
          headers: getApiHeadersFromToken(apiData.token)
        }
      )
    )

    const j =  await response.json()
    return j
  } catch (error) {
    return null
  }
}

export const addMentorResource = async (
  apiData,
  courseSectionId,
  levelId,
  activityId,
  questId,
  resource,
  newFileUpload,
  applyToAllSections
) => {
  try {
    const response = checkHttpStatus(
      await fetch(
        `${getApiBaseUrl()}/api/organizations/${
          apiData.orgId
        }/mentor/course_sections/${courseSectionId}/levels/${levelId}/mentor_activity_resources`,
        {
          method: 'POST',
          headers: getApiHeadersFromToken(apiData.token),
          body: JSON.stringify({
            mentor_activity_resource: {
              ...resource,
              activity_id: activityId,
              quest_id: questId
            },
            new_file_upload: newFileUpload,
            apply_to_all_sections: applyToAllSections
          })
        }
      )
    )

    return await response.json()
  } catch (error) {
    return null
  }
}

export const patchMentorResourceFileShell = async (
  apiData,
  courseSectionId,
  levelId,
  resourceId,
  file
) => {
  try {
    const response = checkHttpStatus(
      await fetch(
        `${getApiBaseUrl()}/api/organizations/${
          apiData.orgId
        }/mentor/course_sections/${courseSectionId}/levels/${levelId}/mentor_activity_resources/${resourceId}/file`,
        {
          method: 'PATCH',
          headers: getApiHeadersFromToken(apiData.token),
          body: JSON.stringify({
            mentor_activity_resource: file
          })
        }
      )
    )

    return await response.json()
  } catch (error) {
    return null
  }
}

export const patchMentorResource = async (
  apiData,
  courseSectionId,
  levelId,
  resourceId,
  resource
) => {
  try {
    const response = checkHttpStatus(
      await fetch(
        `${getApiBaseUrl()}/api/organizations/${
          apiData.orgId
        }/mentor/course_sections/${courseSectionId}/levels/${levelId}/mentor_activity_resources/${resourceId}`,
        {
          method: 'PATCH',
          headers: getApiHeadersFromToken(apiData.token),
          body: JSON.stringify({
            mentor_activity_resource: resource
          })
        }
      )
    )

    return await response.json()
  } catch (error) {
    return null
  }
}

export const deleteMentorResource = async (
  apiData,
  courseSectionId,
  levelId,
  resourceId,
  applyToAllSections
) => {
  try {
    const response = checkHttpStatus(
      await fetch(
        `${getApiBaseUrl()}/api/organizations/${
          apiData.orgId
        }/mentor/course_sections/${courseSectionId}/levels/${levelId}/mentor_activity_resources/${resourceId}`,
        {
          method: 'DELETE',
          headers: getApiHeadersFromToken(apiData.token),
          body: JSON.stringify({
            apply_to_all_sections: applyToAllSections
          })
        }
      )
    )

    return await response.json()
  } catch (error) {
    return null
  }
}
